import React, { Ref, useMemo } from 'react';
import styled from 'styled-components';

import { useTheme } from '@cobalt/react-theme-provider';
import Flex from '@cobalt/react-flex';
import { Input as _DropdownInput } from '@cobalt/react-dropdown';
import { Text } from '@cobalt/react-typography';

import { CascaderSelectProps } from './index';
import CascaderTransIcon from './CascaderTransIcon';

export interface CascaderSelectorProps extends CascaderSelectProps {
  isOpen: boolean;
  inputRef: Ref<HTMLDivElement>;
  selectedValue: string[];
  optionMap: Map<string, string>;
  onToggleOpen: (open?: boolean) => void;
  onClearSelectedValue: () => void;
}

const TransparentDropdownInput = styled(_DropdownInput)<{ loading?: boolean }>`
  transition-property: border, background;

  &:not([disabled]):not([data-co-disabled]):not(&:focus):not([data-co-focus]):not(&:focus-within):not([data-co-focus-within]):not([class*='_co-input--error']) {
    border-color: transparent;

    &[data-co-hover],
    &:hover {
      background-color: var(--gray-200);
    }

    &:not([data-co-hover]):not(&:hover) {
      .trans-icon-c {
        display: ${(props) => (props.loading ? 'flex' : 'none')};
      }
    }
  }

  &[disabled],
  &[data-co-disabled] {
    border-color: transparent;
    background-color: transparent;
  }
`;

const CascaderSelector = (props: CascaderSelectorProps) => {
  const {
    isOpen,
    inputRef,
    selectedValue,
    optionMap,
    onToggleOpen,
    onClearSelectedValue,

    placeholder,
    disabled,
    allowClear,
    loading,
    transparent,
    size,
    variation,
    className,
    style,
  } = props;

  const theme = useTheme();

  const selectedLabel = useMemo(() => {
    return selectedValue.map((str) => optionMap.get(str)).join(' / ');
  }, [optionMap, selectedValue]);

  const DropdownInputC = !transparent ? _DropdownInput : TransparentDropdownInput;

  return (
    <DropdownInputC
      data-testid="cascaderSelector"
      forwardedRef={inputRef}
      disabled={disabled || loading}
      loading={loading}
      size={size}
      variation={variation}
      className={className}
      style={style}
      onClick={() => onToggleOpen()}
    >
      <Flex alignY="center" paddingLeft={1} height="100%">
        {/* Placeholder */}
        {selectedValue.length === 0 && (
          <Text size={size} truncated color={theme.basic500}>
            {placeholder}
          </Text>
        )}

        {/* Value */}
        {selectedValue.length > 0 && (
          <Text data-testid="selectorValue" size={size} truncated title={selectedLabel}>
            {selectedLabel}
          </Text>
        )}

        {!disabled && (
          <CascaderTransIcon
            className="trans-icon-c"
            isOpen={isOpen}
            disabled={disabled!}
            allowClear={allowClear!}
            loading={loading!}
            hasSelected={selectedValue.length > 0}
            size={size}
            onToggleOpen={onToggleOpen}
            onClearSelectedValue={onClearSelectedValue}
          />
        )}
      </Flex>
    </DropdownInputC>
  );
};

export default CascaderSelector;
